// Lazy Toaster wrapper — defers the radix-toast bundle (~52 KB raw)
// until the first toast() call. The toast store buffers in module
// state so calls fired before mount still surface once the chunk
// arrives. See components/ui/LazyToaster.tsx.
import LazyToaster from "@/components/ui/LazyToaster";
// Sonner removed: the lovable template wired up <Sonner /> as a
// second toast surface, but every toast() call in src/ uses the
// radix-based @/hooks/use-toast (which renders through ui/toaster).
// Sonner had no callers and just shipped its runtime + theme code
// for nothing.
//
// TooltipProvider removed: the lovable template wrapped the whole
// app in TooltipProvider, but nothing in src/ renders <Tooltip />
// (everywhere we wanted hover hints we use title=… or our own
// patterns). The empty provider was force-importing radix-tooltip
// + @floating-ui (~132 KB of code) into the entry chunk for zero
// behaviour. Re-add the provider scoped to subtrees if/when an
// actual Tooltip is needed.
import { BrowserRouter } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { ScrollToTop } from "./components/ScrollToTop";
import ErrorBoundary from "@/components/ui/ErrorBoundary";
import BackToTop from "@/components/ui/BackToTop";
import { AppRoutes } from "./routes-client";

// @tanstack/react-query was wired up by the lovable.dev shadcn template
// but never actually used — zero useQuery / useMutation / useInfiniteQuery
// hooks anywhere in src/. Removing the provider drops ~15 KB gz from the
// main bundle. Re-add when the first real query lands.

/**
 * Hidden build indicator (15C) — window global only.
 *
 * The <meta name="or-ctx"> tag is injected server-side by scripts/prerender.mjs
 * so it's present on every prerendered route. This global is for DevTools
 * access: `window.__or` in the console.
 */
if (typeof window !== "undefined") {
  (window as unknown as { __or: { stamp: string; hash: string } }).__or = {
    stamp: __BUILD_STAMP__,
    hash: __BUILD_HASH__,
  };
}

/**
 * Client-side app with BrowserRouter. Routes are code-split via React.lazy
 * (see routes-client.tsx). SSR uses routes-server.tsx with eager imports.
 *
 * This used to pass `future={{ v7_startTransition, v7_relativeSplatPath }}`
 * to opt into v7 behaviour ahead of the major. On React Router 7 both are
 * simply how the router behaves, the `future` prop is gone from
 * BrowserRouterProps, and passing it is a type error. Dropping it is a
 * no-op at runtime — we were already getting the v7 semantics.
 *
 * entry-server.tsx's StaticRouter dropped the same prop in the same
 * commit; the two router elements still have to agree.
 */
const App = () => (
  <HelmetProvider>
    <LazyToaster />
    <BrowserRouter>
      <ScrollToTop />
      <ErrorBoundary>
        <AppRoutes />
      </ErrorBoundary>
      <BackToTop />
    </BrowserRouter>
  </HelmetProvider>
);

export default App;
